import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Trophy } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const [name, setName] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isReturning, setIsReturning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if database needs initialization (only if no players exist)
    const checkAndInitDB = async () => {
      try {
        const response = await axios.get(`${API}/players`);
        if (!response.data || response.data.length === 0) {
          // Only initialize if no players exist
          await axios.post(`${API}/initialize`);
          console.log("Database initialized");
        }
      } catch (e) {
        console.log("DB check/init error:", e);
      }
    };
    checkAndInitDB();
  }, []);

  const handleJoin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/participants/create`, {
        name,
        access_code: accessCode,
      });
      
      localStorage.setItem("participant_id", response.data.id);
      localStorage.setItem("participant_name", name);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to join pool");
    } finally {
      setLoading(false);
    }
  };

  const handleReturningUser = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Get all participants and find by name
      const response = await axios.get(`${API}/admin/participants`);
      const participant = response.data.find(p => p.name === name);
      
      if (participant) {
        localStorage.setItem("participant_id", participant.id);
        localStorage.setItem("participant_name", participant.name);
        navigate("/dashboard");
      } else {
        setError("Name not found. Please check your spelling or create a new entry.");
      }
    } catch (err) {
      setError("Failed to find participant");
    } finally {
      setLoading(false);
    }
  };

  const handleExistingUser = () => {
    const existingId = localStorage.getItem("participant_id");
    if (existingId) {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen relative">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/ae7ebaca-a0c9-4208-a7c9-e983f3731729/images/c210bdfe0256231765dffac5a4ac87f77bdea8205904fd2a3e228c5e834689bd.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#006747] rounded-full mb-4">
                <Trophy className="w-8 h-8 text-white" strokeWidth={1.5} />
              </div>
              <h1 className="text-4xl font-bold text-[#1C2A21] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Masters Pool 2026
              </h1>
              <p className="text-[#4A5B52] mb-4" style={{ fontFamily: "'Figtree', sans-serif" }}>
                {isReturning ? "Welcome back! Enter your name to access your picks" : "Join the pool and make your picks"}
              </p>
              <div className="flex justify-center space-x-4 text-sm">
                <button
                  onClick={() => setIsReturning(false)}
                  className={`pb-1 ${!isReturning ? 'border-b-2 border-[#006747] text-[#006747] font-semibold' : 'text-[#8A9A92]'}`}
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  New Entry
                </button>
                <button
                  onClick={() => setIsReturning(true)}
                  className={`pb-1 ${isReturning ? 'border-b-2 border-[#006747] text-[#006747] font-semibold' : 'text-[#8A9A92]'}`}
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  Returning User
                </button>
              </div>
            </div>

            <form onSubmit={isReturning ? handleReturningUser : handleJoin} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1C2A21] mb-1.5" style={{ fontFamily: "'Figtree', sans-serif" }}>
                  Full Name
                </label>
                <input
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                  placeholder={isReturning ? "Enter your name (e.g., Sean Tweedy)" : "Enter your name"}
                  required
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                />
                {!isReturning && (
                  <p className="mt-1 text-xs text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    You can join up to 3 times with the same name
                  </p>
                )}
              </div>

              {!isReturning && (
                <div>
                  <label className="block text-sm font-medium text-[#1C2A21] mb-1.5" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Access Code
                  </label>
                  <input
                    data-testid="access-code-input"
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E2E8E4] rounded focus:ring-2 focus:ring-[#006747] focus:border-transparent transition-all"
                    placeholder="TWG26!"
                    required
                    style={{ fontFamily: "'Figtree', sans-serif" }}
                  />
                  <p className="mt-1 text-xs text-[#8A9A92]" style={{ fontFamily: "'Figtree', sans-serif" }}>
                    Use code: <span className="font-semibold text-[#006747]">TWG26!</span>
                  </p>
                </div>
              )}

              {error && (
                <div data-testid="error-message" className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                  {error}
                </div>
              )}

              <button
                data-testid="join-pool-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#006747] hover:bg-[#005238] text-white font-semibold py-3 rounded transition-all duration-300 shadow-sm disabled:opacity-50"
                style={{ fontFamily: "'Figtree', sans-serif" }}
              >
                {loading ? (isReturning ? "Finding..." : "Joining...") : (isReturning ? "Access My Picks" : "Join Pool")}
              </button>
            </form>

            {localStorage.getItem("participant_id") && (
              <div className="mt-6 pt-6 border-t border-[#E2E8E4]">
                <button
                  data-testid="return-dashboard-button"
                  onClick={handleExistingUser}
                  className="w-full text-[#006747] hover:text-[#005238] font-medium py-2 transition-all"
                  style={{ fontFamily: "'Figtree', sans-serif" }}
                >
                  Return to Dashboard →
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-[#E2E8E4] text-center space-y-3">
              <a
                data-testid="view-leaderboard-link"
                href="/leaderboard"
                className="block text-[#006747] hover:text-[#005238] font-medium py-2 transition-all"
                style={{ fontFamily: "'Figtree', sans-serif" }}
              >
                View Live Leaderboard →
              </a>
              <a
                data-testid="admin-link"
                href="/admin"
                className="block text-sm text-[#8A9A92] hover:text-[#006747] transition-all"
                style={{ fontFamily: "'Figtree', sans-serif" }}
              >
                Admin Access
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Landing;